import type { Serializer } from "@vueuse/core";
import cloneDeep from "lodash-es/cloneDeep";

import type {
  CategoryFollowedSource,
  EventFollowedType,
  FavouritesOnboardingStartedSource,
} from "~/analytics/segment";

import type { FollowFeatureType } from "./useFollow";

export const ONBOARDING_ENTRY_STORAGE_NAME = "onboardingEntry";

export type AuthDialogContentPages =
  | "login"
  | "register"
  | "forgot-password"
  | "login-with-club";

export enum OnboardingEntryType {
  Login = "login",
  Bid = "bid",
  Follow = "follow",
  AccountCreationPopup = "accountCreationPopup",
  AccountCreationSearch = "accountCreationSearch",
  ClubSso = "clubSso",
  OneTap = "oneTap",
}

type BaseOnboardingEntryData<T extends OnboardingEntryType, D> = {
  type: T;
  data: D & ({ startingAuthPage?: AuthDialogContentPages } | null);
};

type CategoryFollow = {
  id: string;
  name: string;
  followType: FollowFeatureType.Category;
  followSource: CategoryFollowedSource;
  source: FavouritesOnboardingStartedSource;
};

type LoginOnboardingEntryData = BaseOnboardingEntryData<
  OnboardingEntryType.Login,
  { startingAuthPage?: AuthDialogContentPages } | CategoryFollow | null
>;

type BidOnboardingEntryData = BaseOnboardingEntryData<
  OnboardingEntryType.Bid,
  { bidAmount: number | null } | ({ bidAmount: number | null } & CategoryFollow)
>;

type AccountCreationPopupOnboardingEntryData = BaseOnboardingEntryData<
  | OnboardingEntryType.AccountCreationPopup
  | OnboardingEntryType.ClubSso
  | OnboardingEntryType.AccountCreationSearch,
  | CategoryFollow
  | {
      source: FavouritesOnboardingStartedSource;
    }
>;

export type FollowOnboardingEntryData = BaseOnboardingEntryData<
  OnboardingEntryType.Follow,
  | {
      id: string;
      name: string;
      followType: FollowFeatureType.Event | FollowFeatureType.Product;
      followSource: EventFollowedType;
      source: FavouritesOnboardingStartedSource;
    }
  | CategoryFollow
  | {
      source: FavouritesOnboardingStartedSource;
    }
>;

type OneTapOnboardingEntryData = BaseOnboardingEntryData<
  OnboardingEntryType.OneTap,
  null
>;

export type OnboardingEntry =
  | LoginOnboardingEntryData
  | BidOnboardingEntryData
  | FollowOnboardingEntryData
  | AccountCreationPopupOnboardingEntryData
  | OneTapOnboardingEntryData;

const onboardingEntrySerializer: Serializer<OnboardingEntry | undefined> = {
  read: (raw) => {
    if (!raw) return undefined;

    const parsedEntry = JSON.parse(raw);

    // check if the entry is in the OnboardingEntryType enum
    const isValidEntry =
      parsedEntry?.type &&
      Object.values(OnboardingEntryType).includes(parsedEntry.type);

    if (isValidEntry) {
      return parsedEntry;
    }

    return undefined;
  },
  write: (value) => {
    if (!value) return "";

    return JSON.stringify(value);
  },
};

/**
 * "Consumes" state from localStorage and sets it to a ref
 */
const useMemoryRefFromLocalStorage = () => {
  const _persistedOnboardingEntry = useStorageSafely<
    OnboardingEntry | undefined
  >({
    type: "localStorage",
    key: ONBOARDING_ENTRY_STORAGE_NAME,
    defaultValue: undefined,
    options: {
      deep: true,
      serializer: onboardingEntrySerializer,
      flush: "pre",
    },
  });

  // Get state from localStorage and set it to in-memory ref
  const onboardingEntry = ref<OnboardingEntry | undefined>(
    _persistedOnboardingEntry.value
      ? cloneDeep(_persistedOnboardingEntry.value)
      : undefined,
  );

  // Clean up localStorage state
  _persistedOnboardingEntry.value = undefined;

  // If we update the in-memory ref, we want to update the localStorage state
  watch(
    onboardingEntry,
    (newState) => {
      _persistedOnboardingEntry.value = newState;
    },
    {
      deep: true,
    },
  );

  return onboardingEntry;
};

const _useOnboardingDialog = () => {
  const onboardingEntry = useMemoryRefFromLocalStorage();
  const isOnboardingDialogOpen = ref(false);

  const closeOnboardingDialog = () => {
    isOnboardingDialogOpen.value = false;
    onboardingEntry.value = undefined;
  };

  /**
   * Sets the onboarding dialog entry data in the store and triggers the dialog.
   */
  async function openOnboardingDialog(entry?: OnboardingEntry) {
    onboardingEntry.value = entry;

    await nextTick();

    isOnboardingDialogOpen.value = true;
  }

  const { bidVerified, bidLimitInActiveCurrency } = useBidder();

  const userCanPlaceTheirBid = computed(() => {
    if (
      !onboardingEntry.value ||
      onboardingEntry.value.type !== OnboardingEntryType.Bid
    )
      return false;

    if (
      onboardingEntry.value.data === undefined ||
      onboardingEntry.value.data?.bidAmount === null ||
      bidLimitInActiveCurrency.value === undefined
    ) {
      return bidVerified.value;
    }

    return (
      bidLimitInActiveCurrency.value >= onboardingEntry.value.data.bidAmount
    );
  });

  return {
    onboardingEntry,
    closeOnboardingDialog,
    openOnboardingDialog,
    userCanPlaceTheirBid,
    isOnboardingDialogOpen,
  };
};

export default createSharedComposable(_useOnboardingDialog);
